/**
 * Een xlsx-bestand lezen in de browser.
 *
 * Bewust een eigen, kleine lezer in plaats van een spreadsheetbibliotheek: we hebben
 * één ding nodig — de cellen van één blad als tekst — en dat is een zipbestand met wat
 * XML erin. Een volledige xlsx-bibliotheek zou tientallen keren zo groot zijn en veel
 * meer kunnen dan hier ooit gebruikt wordt.
 *
 * Wat wél wordt afgehandeld: de gedeelde-strings-tabel (Excel zet tekst daar neer in
 * plaats van in de cel), inline strings, formulecellen met een bewaarde uitkomst, en
 * gaten in de rij- en kolomnummering. Datums komen als getal terug; die komen in de
 * houtstaat niet voor.
 */
import { unzipSync, strFromU8 } from 'fflate';

export interface Blad {
  naam: string;
  /** Rijen met cellen als tekst; ontbrekende cellen zijn een lege string. */
  rijen: string[][];
}

/** Kolomletter naar index: A → 0, Z → 25, AA → 26. */
function kolomIndex(verwijzing: string): number {
  const letters = /^([A-Z]+)/.exec(verwijzing)?.[1] ?? 'A';
  let index = 0;
  for (const letter of letters) index = index * 26 + (letter.charCodeAt(0) - 64);
  return index - 1;
}

const ontsnap = (tekst: string): string =>
  tekst
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_h, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&amp;/g, '&');

/** Alle `<t>`-inhoud van één `<si>`-element aan elkaar (rich text is opgesplitst). */
function tekstUit(xml: string): string {
  const delen = [...xml.matchAll(/<t[^>]*>([\s\S]*?)<\/t>/g)].map((m) => m[1] ?? '');
  return ontsnap(delen.join(''));
}

/**
 * Leest alle bladen van een xlsx. Geeft per blad de naam en de cellen als tekst.
 */
export function leesXlsx(bestand: ArrayBuffer): Blad[] {
  const bestanden = unzipSync(new Uint8Array(bestand));
  const lees = (naam: string): string | null => {
    const inhoud = bestanden[naam];
    return inhoud ? strFromU8(inhoud) : null;
  };

  const workbook = lees('xl/workbook.xml');
  if (!workbook) throw new Error('Dit lijkt geen Excel-bestand (xl/workbook.xml ontbreekt).');

  // Blad → bestand loopt via de relaties; de volgorde in workbook.xml is niet die van
  // de bestandsnamen.
  const relaties = new Map<string, string>();
  for (const m of (lees('xl/_rels/workbook.xml.rels') ?? '').matchAll(
    /Id="([^"]+)"[^>]*Target="([^"]+)"/g,
  )) {
    relaties.set(m[1] ?? '', (m[2] ?? '').replace(/^\/?xl\//, '').replace(/^\.\//, ''));
  }

  const gedeeld = [...(lees('xl/sharedStrings.xml') ?? '').matchAll(/<si>([\s\S]*?)<\/si>/g)].map(
    (m) => tekstUit(m[1] ?? ''),
  );

  const bladen: Blad[] = [];
  for (const m of workbook.matchAll(/<sheet[^>]*name="([^"]*)"[^>]*r:id="([^"]+)"[^>]*\/>/g)) {
    const naam = ontsnap(m[1] ?? '');
    const doel = relaties.get(m[2] ?? '');
    const xml = doel ? lees(`xl/${doel}`) : null;
    if (!xml) continue;

    const rijen: string[][] = [];
    for (const rij of xml.matchAll(/<row[^>]*r="(\d+)"[^>]*>([\s\S]*?)<\/row>/g)) {
      const nummer = Number(rij[1]) - 1;
      const cellen: string[] = [];
      for (const cel of (rij[2] ?? '').matchAll(/<c([^>]*)>([\s\S]*?)<\/c>|<c([^>]*)\/>/g)) {
        const kop = cel[1] ?? cel[3] ?? '';
        const lijf = cel[2] ?? '';
        const verwijzing = /r="([A-Z]+)\d+"/.exec(kop)?.[1];
        const index = verwijzing ? kolomIndex(verwijzing) : cellen.length;
        const soort = /t="([^"]+)"/.exec(kop)?.[1];

        let waarde = '';
        if (soort === 's') {
          const nr = Number(/<v>([\s\S]*?)<\/v>/.exec(lijf)?.[1] ?? '-1');
          waarde = gedeeld[nr] ?? '';
        } else if (soort === 'inlineStr') {
          waarde = tekstUit(lijf);
        } else {
          // Ook een formulecel heeft een `<v>` met de laatst berekende uitkomst.
          waarde = ontsnap(/<v>([\s\S]*?)<\/v>/.exec(lijf)?.[1] ?? '');
        }
        while (cellen.length < index) cellen.push('');
        cellen[index] = waarde.trim();
      }
      while (rijen.length < nummer) rijen.push([]);
      rijen[nummer] = cellen;
    }
    bladen.push({ naam, rijen });
  }

  return bladen;
}
